/* eslint-disable @nx/enforce-module-boundaries */
// TODO: dex-tech-store-access-selectors & dex-tech-utils-routing are not shared libs.
//       We should move them, however they are full of TechRef specific state
import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import { ChevronDown } from '@squareup/dex-icons/dex/control';
import { Spinner } from '@squareup/dex-icons/dex/misc';
import { ExclamationCircle } from '@squareup/dex-icons/market/alert';
import { useSelectSpecVersionInView } from '@squareup/dex-tech-store-access-selectors';
import { versionInfoToVersionPath } from '@squareup/dex-tech-utils-routing';
import { NativeButton } from '@squareup/dex-ui';
import { useIsMarketComponentLoaded } from '@squareup/dex-ui-hooks';
import { Box, Paragraph20 } from '@squareup/dex-ui-shared-base';
import { commonIconStyles } from '@squareup/dex-ui-shared-icon-styles';
import {
  MarketDropdown,
  MarketList,
  MarketPopover,
  MarketRow,
  MarketTooltip,
} from '@squareup/dex-ui-shared-market';
import { usePathname } from '@squareup/dex-utils-shared-routing-hooks';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import React, { memo, useRef } from 'react';

import styles from './spec-version-picker.module.css';

const MAX_VERSIONS = 5;

interface Props {
  variant: 'reference' | 'api-explorer';
}

const SpecVersionPickerContainer: React.FC<Props> = memo(({ variant }) => {
  // The spec and graph lib do not carry the namespace for some reason, only version. So use the spec version info to get it :(
  const {
    versionInfo,
    parsedInfo,
    error,
    isLoading: isVersionSelectionLoading,
  } = useSelectSpecVersionInView();

  const { pathName } = usePathname();
  const router = useRouter();

  const { data: versions, isLoading: isVersionsDataLoading } =
    getDeveloperApi().useGetSpecificationVersionsQuery(undefined);

  const ref = useRef<globalThis.HTMLMarketDropdownElement>(null);
  const [isMarketComponentLoaded, onMarketRef] = useIsMarketComponentLoaded(
    ref,
    'market-dropdown'
  );

  const selectedVersion = parsedInfo?.version || '';
  const isSpecVersionLoadingOrError = isVersionSelectionLoading || error;
  const latestVersion = versions?.at(0);
  const isVersionSelectionOld =
    selectedVersion && latestVersion && selectedVersion !== latestVersion;

  const Version = () => (
    <Paragraph20
      as="span"
      margin={{ right: '0.5x' }}
      className={clsx(
        styles['version'],
        variant === 'api-explorer' && styles['hide-small']
      )}
    >
      API version
    </Paragraph20>
  );

  return (
    <Box className={styles['button-group']}>
      <Box
        className={clsx(styles['container'], {
          [styles['hidden'] as string]: !isMarketComponentLoaded,
        })}
      >
        <MarketDropdown
          aria-label="Pick API version"
          className={styles['dropdown'] as string}
          popover-placement="bottom-end"
          ref={onMarketRef}
        >
          <NativeButton
            slot="trigger"
            className={clsx(
              styles[`version-picker-${variant}`],
              isVersionSelectionOld &&
                variant === 'api-explorer' &&
                styles['no-padding']
            )}
            data-testid="spec-version-picker"
            trackingId="spec-version-picker"
          >
            {isVersionSelectionOld ? (
              <>
                <MarketTooltip popoverSkidding={75}>
                  <Box className={styles['content']} as="div" slot="content">
                    You are viewing an old API version
                  </Box>
                  <ExclamationCircle
                    className={clsx(
                      styles['exclamation-circle'],
                      variant === 'api-explorer' &&
                        styles['exclamation-circle-api-explorer']
                    )}
                    slot="trigger"
                    viewBox="0 0 24 24"
                  />
                </MarketTooltip>
                <Version />
              </>
            ) : (
              <Version />
            )}
            {isVersionSelectionLoading ? (
              <Spinner className={commonIconStyles['icon-spinner']} />
            ) : (
              <Paragraph20
                as="span"
                margin={{ right: '0.5x' }}
                data-testid="version-picker-selected-version"
              >
                {selectedVersion}
              </Paragraph20>
            )}
            {isVersionsDataLoading ? (
              <Spinner className={commonIconStyles['icon-spinner']} />
            ) : (
              <ChevronDown
                className={styles['icon']}
                data-testid="spec-picker-chevron"
              />
            )}
          </NativeButton>
          <MarketPopover slot="popover">
            <MarketList data-testid="spec-version-picker-list" transient>
              {versions?.slice(0, MAX_VERSIONS).map((version) => {
                const currentSquareVersionPath =
                  versionInfoToVersionPath(versionInfo);
                const destinationSquareVersionPath = versionInfoToVersionPath({
                  version,
                  namespace: versionInfo?.namespace,
                });

                const canBuildHref =
                  pathName &&
                  !isSpecVersionLoadingOrError &&
                  destinationSquareVersionPath &&
                  currentSquareVersionPath;

                const href = canBuildHref
                  ? pathName.replace(
                      currentSquareVersionPath,
                      destinationSquareVersionPath
                    )
                  : undefined;
                return (
                  <MarketRow
                    key={version}
                    value={version}
                    trackingId={`spec-version-${version}`}
                    interactive
                    size="small"
                    selected={version === selectedVersion}
                    onClick={() => {
                      if (href) {
                        router.push(href);
                      }
                    }}
                  >
                    <Paragraph20
                      className={
                        version === selectedVersion
                          ? styles['selected-option']
                          : ''
                      }
                      weight={'semi-bold'}
                    >
                      {version}
                    </Paragraph20>
                  </MarketRow>
                );
              })}
              <MarketRow
                interactive
                trackingId={'spec-version-all-versions'}
                key={'All versions'}
                value={'All versions'}
                size="small"
                href={'/docs/api/artifacts'}
              >
                <Paragraph20
                  weight={'semi-bold'}
                  className={styles['all-versions-option']}
                >
                  {'All versions ->'}
                </Paragraph20>
              </MarketRow>
            </MarketList>
          </MarketPopover>
        </MarketDropdown>
      </Box>
    </Box>
  );
});

SpecVersionPickerContainer.displayName = 'SpecVersionPickerContainer';

export { SpecVersionPickerContainer };
